import type { Arguments } from 'swr';

const getMutatorForType =
  (type: string) => (idToRevalidate: string | null | undefined) => (key: Arguments) => {
    if (!Array.isArray(key)) return false;

    const [keyType, idOrParams] = key;

    if (keyType !== type) return false;

    // If the key is just `type`, then we want to revalidate: this is the
    // list of all types.
    if (key.length === 1) return true;

    // If the key is `type` and params, then we want to revalidate: this is
    // the list of all types with the search params.
    if (typeof idOrParams === 'object') return true;

    // `[type, 'property', propertyId]` is the type's list scoped to one property, not
    // a record id in the second slot — that is the key `useContractsForProperty` reads
    // under. A save can add to or drop from any such list and this matcher only knows
    // the saved record's own id, so match them all; SWR refetches only the keys
    // actually mounted.
    if (key.length === 3 && idOrParams === 'property') return true;

    // If the key has an ID that matches the one we want to revalidate, then
    // Notably, this also revalidates its Tickets, Notes, and Attachments.
    if (idOrParams === idToRevalidate) return true;

    // Otherwise, we don't want to revalidate.
    return false;
  };

export const getInvoiceMutator = getMutatorForType('invoice');

// A credit shows on its invoice's page (Credits card, net amount) and in the
// creditable-invoice picker. Which invoice it credited isn't always known (a
// delete has only the credit's id), so every mounted invoice record refetches.
export const creditLinksMutator = (key: Arguments) =>
  Array.isArray(key) &&
  (key[0] === 'creditableInvoices' || (key[0] === 'invoice' && typeof key[1] === 'string'));
export const getTicketMutator = getMutatorForType('ticket');
export const getContractMutator = getMutatorForType('contract');
export const getCertificateMutator = getMutatorForType('certificate');
